import { Stack } from '@mui/material'
import { DecoratedValue } from 'ComponentLibrary/src/atoms/DecoratedValue'
import { Dropdown } from 'ComponentLibrary/src/atoms/Dropdown/Dropdown'
import { OrderStatusIcon } from 'ComponentLibrary/src/atoms/OrderStatus'
import { assembleAssetTitle } from 'ComponentLibrary/src/common/address'
import { isoDateStringToLocalFormat } from 'ComponentLibrary/src/common/date'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { Typography } from 'ComponentLibrary/src/piazza'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { ComponentProps } from 'react'
import invariant from 'tiny-invariant'
import { OrderFilterOptionSchema } from 'ComponentLibrary/src/common/schemas.zod'
import z from 'zod'

export type OrderFilterOption = z.infer<typeof OrderFilterOptionSchema>

type OrderFilterProps = {
    orders: OrderFilterOption[]
    filterValue: UUID | null
    setFilterValue: (newValue: UUID | null) => void
    onOpen?: () => void
    onClose?: () => void
    isLoadingOrderData?: boolean
    required?: boolean
    size?: ComponentProps<typeof Dropdown>['size']
    disabled?: boolean
    copy?: Partial<OrderDropdownCopy>
    locale?: string
}

export type OrderDropdownCopy = {
    loading: string
    label: string
    noOptions: string
    valuationDate: string
    orderDate: string
}

const DEFAULT_ORDER_DROPDOWN_COPY: OrderDropdownCopy = {
    loading: 'Lade Auftragsdaten...',
    label: 'Auftrag',
    noOptions: 'Keine Aufträge gefunden',
    valuationDate: 'Stichtag',
    orderDate: 'Auftragsdatum',
}

export function OrderDropdown({
    orders,
    filterValue,
    setFilterValue,
    isLoadingOrderData = false,
    onClose,
    onOpen,
    required = false,
    size = 'small',
    disabled,
    copy,
    locale = 'de-DE',
}: OrderFilterProps) {
    const resolvedCopy = { ...DEFAULT_ORDER_DROPDOWN_COPY, ...copy }
    function getOptionLabel(option: UUID) {
        const orderOption = orders.find((order) => order.uuid === option)
        return assembleAssetTitle({
            customer_asset_reference: orderOption?.customer_asset_reference,
            ...orderOption?.address,
        })
    }

    const orderIds = orders.map((orderOption) => orderOption.uuid)

    return (
        <Dropdown
            disabled={disabled}
            onOpen={onOpen}
            onClose={onClose}
            loading={isLoadingOrderData}
            loadingText={resolvedCopy.loading}
            options={orderIds}
            required={required}
            label={resolvedCopy.label}
            size={size}
            getOptionLabel={getOptionLabel}
            fullWidth
            noOptionsText={resolvedCopy.noOptions}
            renderOption={(props, option) => {
                const orderOption = orders.find((order) => order.uuid === option)
                invariant(orderOption !== undefined, 'orderOption cannot be undefined')
                return (
                    <li {...props} key={orderOption.uuid}>
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                            <OrderStatusIcon variant="square" state={orderOption.status} />
                            <Stack>
                                <Typography variant="textS" color={tokens.ColorTextBold}>
                                    {getOptionLabel(option)}
                                </Typography>
                                <Stack direction="row" gap="20px">
                                    {orderOption.valuation_date && (
                                        <DecoratedValue variant="bodyTextSmall" color={tokens.ColorTextDefault} endDecorator="|">
                                            {`${resolvedCopy.valuationDate}: ${isoDateStringToLocalFormat(
                                                orderOption?.valuation_date,
                                                false,
                                                locale
                                            )}`}
                                        </DecoratedValue>
                                    )}
                                    <DecoratedValue variant="bodyTextSmall" color={tokens.ColorTextDefault}>
                                        {`${resolvedCopy.orderDate}: ${isoDateStringToLocalFormat(orderOption.order_date, false, locale)}`}
                                    </DecoratedValue>
                                </Stack>
                            </Stack>
                        </Stack>
                    </li>
                )
            }}
            value={filterValue}
            onChange={(_, value) => setFilterValue(value)}
        />
    )
}
